import React from 'react';
import { observer } from 'mobx-react-lite';
import { ParentSize } from '@visx/responsive';
import { TextProps } from '@visx/text';
import { scaleOrdinal } from '@visx/scale';
import { ThemeConfig } from '@visx/xychart/lib/theme/buildChartTheme';
import { LineChartDataProps, XYSeriesChart } from '../xySeriesChart';
import { DropdownInputControlled } from '../../input';
import { AudienceFocusChartState } from './audienceFocusChartState';
import { AudienceFocusChartDatum } from './audienceFocusChartSchema';
import { EllipseText } from '../utils/ellipseText';
import { AudienceFocusTooltip } from '.';
import { DefaultThemeConfig } from '../utils/defaultThemeConfig';
import { ChartWrapper } from '../chartWrapper';

type Props = {
  model: AudienceFocusChartState;
}

export const AudienceFocusChart = observer(({ model }: Props) => {
  const { dataSource } = model;
  const teamId = model.params?.teamId;
  const { isFetching: showLoadMask, error } = dataSource;

  // NOTE: do not access model.outputData directly in XYSeriesChart
  // this is because XYSeriesChart is rendered within a callback to ParentSize, which I suspect is run async
  const data = model.outputData;

  const hasData = !!(data?.[0]?.data?.length > 0);
  const showNoDataMask = !hasData && !showLoadMask;

  let overlayMessage;
  if (error) {
    overlayMessage = { code: 'Error', message: 'An error has occured' };
  } else if (showNoDataMask) {
    overlayMessage = { code: 'NoData' };
  }

  const tickLabelPropsObj: Partial<TextProps> = {
    width: 260,
    textAnchor: 'end',
    // verticalAnchor: 'middle',
  };

  const themeConfig: ThemeConfig = {
    backgroundColor: '#ffffff',
    tickLength: 8,
    colors: [],
    gridColor: '',
    gridColorDark: '',
  };

  const domain = (data[0]?.data?.map(
    (item: LineChartDataProps) => item.date
  ) ?? []) as string[];

  const scaleBandRangeByMaxBandWidthObj = {
    maxBandWidth: 20,
    domain,
  };

  const colorAccessor = (key: unknown) => {
    const colorScale = scaleOrdinal({
      domain,
      range: DefaultThemeConfig.colors
    });

    return colorScale((key as AudienceFocusChartDatum)?.date as string);
  };

  const chartTitle = teamId ? `My Library's Interests` : `My Viewers' Interests`;

  const tooltipContent = teamId ?
    `Find out what your viewers care about in the most viewed and bookmarked topics from your library's videos.` :
    `Find out what your viewers care about in the most viewed and bookmarked topics from your videos.`;

  const dropdownInput = <DropdownInputControlled
    className="form-input"
    model={model.chartTypeInput}
    showFeedback={false}
    notDeselectable={true}
    persistentFeedback={false} />;

  const tickComponent = ({ formattedValue, ...tickProps }: { formattedValue?: string; x?: number; y?: number; [key: string]: unknown }) => (
    <EllipseText
      x={tickProps.x}
      y={tickProps.y}
      suffix="..."
      className="chartLabel"
      width={160}
      style={{
        fontWeight: 600,
        fontSize: '12px',
      }}>
      {formattedValue}
    </EllipseText>
  )

  const dateAxisProps = {
    tickLabelProps: () => tickLabelPropsObj,
    numTicks: data[0]?.data?.length ?? 3,
    tickComponent,
  };

  const valueAxisProps = {
    tickLabelProps: () => tickLabelPropsObj,
    numTicks: 3,
  }

  return (
    <ChartWrapper
      title={chartTitle}
      tooltipContent={tooltipContent}
      showLoadMask={showLoadMask}
      overlayMessage={overlayMessage}
      tools={dropdownInput}>
      <ParentSize>{(parent) =>
        <XYSeriesChart
          className="horizontalBarChart"
          renderBarSeries={true}
          renderLineSeries={false}
          renderHorizontally={true}
          showLegend={false}
          showGridRows={false}
          showGridColumns={true}
          sharedTooltip={false}
          margin={{ top: 20, right: 20, bottom: 25, left: 190 }}
          dateAxisProps={dateAxisProps}
          valueAxisProps={valueAxisProps}
          colorAccessor={colorAccessor}
          renderTooltip={(params) => <AudienceFocusTooltip {...params} />}
          scaleBandRangeByMaxBandWidth={scaleBandRangeByMaxBandWidthObj}
          height={parent.height - 32}
          width={parent.width}
          series={data}
          themeConfig={themeConfig} />
      }</ParentSize>
    </ChartWrapper>
  );
});
